/**
 * Lo que paga el cliente y el margen por cuenta. **Solo admin.**
 *
 * Es el único bloque de la pantalla que no ven los tres roles, y el que explica
 * dónde se puso el límite de la excepción de permisos de este módulo. El costo de
 * servir se comparte porque habilita decisiones de eficiencia; el precio no,
 * porque **con el costo al lado el precio hace derivable el MRR por cuenta con
 * una resta**, y eso es la facturación con otro nombre.
 *
 * Por eso el corte es un bloque entero y no una columna condicional metida en la
 * tabla de consumo: un solo lugar donde el recorte puede fallar, y una lectura
 * evidente al abrir el archivo. El recorte real lo hace el servidor —
 * `economiaPorCuenta` no viaja en el payload de ops ni de comercial, y `stripMoney`
 * lo borra igual—, así que esto no es la autoridad: es la consecuencia.
 *
 * El margen publica sus dos términos en la misma fila: `precio − costo`. Un
 * porcentaje de margen sin el precio del que sale no se puede discutir.
 */
import type { UsageEconomia } from '@ops/contracts'
import { formatMetricValue } from '@ops/contracts'

import { rutaCliente } from '../../lib/rutas'
import {
  DataTable,
  MissingCell,
  Panel,
  StatusPill,
  type Column,
} from '../../ui'
import { Vacio } from '../comunes'
import { severidadDeMargen } from './derivar'
import { Link } from 'react-router-dom'

export interface EconomiaBlockProps {
  filas: readonly UsageEconomia[]
  id?: string
}

export function EconomiaBlock({ filas, id }: EconomiaBlockProps) {
  const columnas: ReadonlyArray<Column<UsageEconomia>> = [
    {
      key: 'cuenta',
      header: 'Cuenta',
      rowHeader: true,
      width: '220px',
      sortValue: (f) => f.clientNombre,
      filterValue: (f) => f.clientNombre,
      render: (f) => (
        <Link
          to={rutaCliente(f.clientId)}
          className="truncate font-semibold text-accent-text underline decoration-from-font underline-offset-2 hover:text-text"
        >
          {f.clientNombre}
        </Link>
      ),
    },
    {
      key: 'precio',
      header: 'Lo que paga',
      align: 'right',
      width: '150px',
      sortValue: (f) => f.precioUsd,
      render: (f) =>
        f.precioUsd === null ? (
          <MissingCell que="precio de la cuenta" />
        ) : (
          <span className="tabular">{formatMetricValue(f.precioUsd, 'usd')}</span>
        ),
    },
    {
      key: 'costo',
      header: 'Costo de servir',
      align: 'right',
      width: '150px',
      sortValue: (f) => f.costoUsd,
      render: (f) =>
        f.costoUsd === null ? (
          <MissingCell que="medición del período" />
        ) : (
          <span className="tabular">{formatMetricValue(f.costoUsd, 'usd')}</span>
        ),
    },
    {
      key: 'margen',
      header: 'Margen',
      align: 'right',
      width: '250px',
      sortValue: (f) => f.margenPct,
      render: (f) =>
        f.margenUsd === null ? (
          <MissingCell que="precio o costo de la cuenta" />
        ) : (
          <span className="flex flex-col items-end gap-0.5">
            <span className="flex items-center gap-2">
              <span className="tabular font-semibold">
                {formatMetricValue(f.margenUsd, 'usd')}
              </span>
              {/* El color nunca solo: el porcentaje va escrito en la pill. */}
              <StatusPill
                severidad={severidadDeMargen(f.margenPct)}
                size="sm"
                testId="margen-pill"
              >
                {f.margenPct === null
                  ? 'sin porcentaje'
                  : formatMetricValue(f.margenPct, 'percent')}
              </StatusPill>
            </span>
            {/* La resta entera, que es lo que hace al margen discutible. */}
            <span
              data-testid="margen-base"
              className="tabular text-micro text-muted-strong"
            >
              {formatMetricValue(f.precioUsd, 'usd')} −{' '}
              {formatMetricValue(f.costoUsd, 'usd')}
            </span>
          </span>
        ),
    },
  ]

  if (filas.length === 0) {
    return (
      <div id={id} className="min-w-0">
        <Panel titulo="Margen por cuenta" padding="sm">
          <Vacio
            icono={null}
            punteada
            mensaje="Ninguna cuenta tiene precio y costo cargados a la vez"
            detalle="El margen necesita los dos términos: el precio sale de la suscripción y el costo, de las trazas medidas."
          />
        </Panel>
      </div>
    )
  }

  return (
    <div id={id} className="min-w-0">
      <Panel
        titulo="Margen por cuenta"
        subtitulo="Solo admin. Es el corte de la excepción de permisos de esta pantalla: el costo de servir lo ven los tres roles, lo que paga el cliente no."
        padding="none"
        footer={
          <>
            El margen es <b>precio − costo</b>, con los dos términos en la fila. El
            costo es el estimado por tarifa de catálogo del bloque de modelos, no
            el facturado por el proveedor: el margen real puede ser algo distinto.
          </>
        }
      >
        <DataTable
          columns={columnas}
          rows={filas}
          rowKey={(f) => f.clientId}
          density="compacta"
          ordenInicial={{ key: 'margen', dir: 'asc' }}
          filtroPlaceholder="cuenta"
          emptyMessage="Ninguna cuenta con precio y costo cargados"
          caption="Lo que paga cada cuenta, lo que cuesta servirla y el margen que queda. El precio sale de la suscripción; el costo, de las trazas medidas por Langfuse."
        />
      </Panel>
    </div>
  )
}
